import { Box, ButtonBase, Collapse, SvgIcon, Chip, Fade } from '@mui/material';
import ChevronRightIcon from '@untitled-ui/icons-react/build/esm/ChevronRight';
import PropTypes from 'prop-types';
import type { FC, ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { RouterLink } from 'src/components/router-link';

interface SideNavItemProps {
  active?: boolean;
  children?: ReactNode;
  depth?: number;
  disabled?: boolean;
  external?: boolean;
  icon?: ReactNode;
  label?: ReactNode;
  open?: boolean;
  path?: string;
  title: string;
}

export const SideNavItem: FC<SideNavItemProps> = (props) => {
  const { active, children, depth = 0, disabled, external, icon, label, open: openProp, path, title } = props;
  const [open, setOpen] = useState<boolean>(!!openProp);

  const handleToggle = useCallback((): void => {
    setOpen((prevOpen) => !prevOpen);
  }, []);

  // Icons can be defined at top level only, deep levels have bullets instead of actual icons.

  let startIcon: ReactNode;

  if (depth === 0) {
    startIcon = icon;
  } else {
    startIcon = (
      <Box
        sx={{
          alignItems: 'center',
          display: 'center',
          height: 20,
          justifyContent: 'center',
          width: 20,
        }}
      >
        <Box
          sx={{
            backgroundColor: 'var(--nav-item-icon-color)',
            borderRadius: '50%',
            height: 4,
            opacity: 0, // remove this if you want it to be visible
            width: 4,
            ...(active && {
              backgroundColor: 'var(--nav-item-icon-active-color)',
              height: 6,
              opacity: 1,
              width: 6,
            }),
          }}
        />
      </Box>
    );
  }

  const offset = depth === 0 ? 0 : (depth - 1) * 16;

  // Branch

  if (children) {
    return (
      <li>
        <ButtonBase
          disabled={disabled}
          onClick={handleToggle}
          sx={{
            alignItems: 'center',
            borderRadius: 2,
            display: 'flex',
            justifyContent: 'flex-start',
            pl: `${16 + offset}px`,
            pr: '12px',
            py: '8px',
            textAlign: 'left',
            width: '100%',
            position: 'relative',
            transition: 'all 0.2s ease',
            mb: 0.5,
            ...(active && {
              ...(depth === 0 && {
                backgroundColor: 'var(--nav-item-active-bg)',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 3,
                  height: '60%',
                  backgroundColor: 'var(--nav-item-icon-active-color)',
                  borderRadius: '0 2px 2px 0',
                },
              }),
            }),
            '&:hover': {
              backgroundColor: 'var(--nav-item-hover-bg)',
              transform: 'translateX(2px)',
            },
          }}
        >
          {startIcon && (
            <Box
              component="span"
              sx={{
                alignItems: 'center',
                color: 'var(--nav-item-icon-color)',
                display: 'inline-flex',
                justifyContent: 'center',
                mr: 2,
                ...(active && {
                  color: 'var(--nav-item-icon-active-color)',
                }),
              }}
            >
              {startIcon}
            </Box>
          )}
          <Box
            component="span"
            sx={{
              color: 'var(--nav-item-color)',
              flexGrow: 1,
              fontFamily: (theme) => theme.typography.fontFamily,
              fontSize: depth > 0 ? 13 : 14,
              fontWeight: depth > 0 ? 500 : 600,
              lineHeight: '24px',
              whiteSpace: 'nowrap',
              ...(active && {
                color: 'var(--nav-item-active-color)',
              }),
              ...(disabled && {
                color: 'var(--nav-item-disabled-color)',
              }),
            }}
          >
            {title}
          </Box>
          <SvgIcon
            sx={{
              color: 'var(--nav-item-chevron-color)',
              fontSize: 16,
              ml: 2,
              transition: 'transform 0.2s ease',
              ...(open && {
                transform: 'rotate(180deg)',
                color: 'var(--nav-item-icon-active-color)',
              }),
            }}
          >
            <ChevronRightIcon />
          </SvgIcon>
        </ButtonBase>
        <Collapse
          in={open}
          sx={{
            mt: 0.5,
            '& .MuiCollapse-wrapper': {
              transition: 'all 0.3s ease',
            },
          }}
        >
          <Fade in={open} timeout={300}>
            <Box
              sx={{
                pl: 1,
                borderLeft: '2px solid var(--nav-border-color)',
                ml: 2,
                borderRadius: '0 0 0 8px',
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -2,
                  top: 0,
                  bottom: 0,
                  width: 2,
                  background: 'linear-gradient(to bottom, var(--nav-item-icon-active-color), transparent)',
                  opacity: 0.3,
                },
              }}
            >
              {children}
            </Box>
          </Fade>
        </Collapse>
      </li>
    );
  }

  // Leaf

  const linkProps = path
    ? external
      ? {
          component: 'a',
          href: path,
          target: '_blank',
        }
      : {
          component: RouterLink,
          href: path,
        }
    : {};

  return (
    <li>
      <ButtonBase
        disabled={disabled}
        sx={{
          alignItems: 'center',
          borderRadius: 2,
          display: 'flex',
          justifyContent: 'flex-start',
          pl: `${16 + offset}px`,
          pr: '12px',
          py: '8px',
          textAlign: 'left',
          width: '100%',
          position: 'relative',
          transition: 'all 0.2s ease',
          mb: 0.5,
          ...(active && {
            ...(depth === 0 && {
              backgroundColor: 'var(--nav-item-active-bg)',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 3,
                height: '60%',
                backgroundColor: 'var(--nav-item-icon-active-color)',
                borderRadius: '0 2px 2px 0',
              },
            }),
          }),
          '&:hover': {
            backgroundColor: 'var(--nav-item-hover-bg)',
            transform: 'translateX(2px)',
          },
        }}
        {...linkProps}
      >
        {startIcon && (
          <Box
            component="span"
            sx={{
              alignItems: 'center',
              color: 'var(--nav-item-icon-color)',
              display: 'inline-flex',
              justifyContent: 'center',
              mr: 2,
              ...(active && {
                color: 'var(--nav-item-icon-active-color)',
              }),
            }}
          >
            {startIcon}
          </Box>
        )}
        <Box
          component="span"
          sx={{
            color: 'var(--nav-item-color)',
            flexGrow: 1,
            fontFamily: (theme) => theme.typography.fontFamily,
            fontSize: depth > 0 ? 13 : 14,
            fontWeight: depth > 0 ? 500 : 600,
            lineHeight: '24px',
            whiteSpace: 'nowrap',
            ...(active && {
              color: 'var(--nav-item-active-color)',
            }),
            ...(disabled && {
              color: 'var(--nav-item-disabled-color)',
            }),
          }}
        >
          {title}
        </Box>
        {label && (
          <Chip
            size="small"
            label={label}
            sx={{
              ml: 'auto',
              height: 20,
              fontSize: '0.7rem',
              fontWeight: 600,
              backgroundColor: 'var(--nav-item-hover-bg)',
              color: 'var(--nav-item-color)',
              border: '1px solid var(--nav-border-color)',
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        )}
      </ButtonBase>
    </li>
  );
};

SideNavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number,
  disabled: PropTypes.bool,
  external: PropTypes.bool,
  icon: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
};
